import React, { useState, useEffect, useRef } from "react";
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Box, Typography } from "@mui/material";
import { black, neutral400, primary500, white } from "../../lib/rebrandColors/colors";
import Tooltip from "@mui/material/Tooltip";
import { useLocation } from "react-router-dom";

export default function NewWeeklyScheduleView({
    date = dayjs(),
    filteredSchedules,
    org,
    outlets,
    orgData,
    scheduleClick,
    filter,
    events,
    setIsEventEditModalOpen,
    setSelectedEvent,
}) {
    const scrollRef = useRef(null);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const columns = Array(7).fill(null); // 7 columns
    const location = useLocation();

    dayjs.extend(isToday);
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const month = dayjs(date).month(); // Extract month from date  
    const year = dayjs(date).year(); // Extract year from date
    const monday = dayjs(date).startOf('week').add(1, 'day');
    const weekDates = Array.from({ length: 7 }, (_, i) => monday.add(i, 'day'));
    const weekDateStrings = weekDates.map(d => d.format('YYYY-MM-DD'));
    const formatHour = (hour) => {
        const period = hour >= 12 ? "PM" : "AM";
        const formattedHour = hour % 12 || 12;
        return `${formattedHour} ${period}`;
    };
    const userTimezone = dayjs.tz.guess();
    const orgTimezone = orgData[0]?.timezone || "America/New_York";

    // Get current UTC offsets in minutes
    const userOffset = dayjs().tz(userTimezone).utcOffset();
    const orgOffset = dayjs().tz(orgTimezone).utcOffset();

    // Convert to hours
    const timezoneHourDifference = (userOffset - orgOffset) / 60;
    const currentHour = dayjs().tz(userTimezone).hour();
    // sets scroll
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const currentDate = new Date();
        const hour = currentDate.getHours();
        const minutes = currentDate.getMinutes();

        const totalHeight = container.scrollHeight; // Total scrollable content height
        const pixelsPerHour = totalHeight / 24;
        const scrollPosition = hour * pixelsPerHour + (minutes / 60) * pixelsPerHour;

        container.scrollTop = scrollPosition;
    }, []);
    // dynamic column widths
    const columnWidthBase = 170;
    const columnWidthExpand = 402;
    const columnWidthShrink = 134;

    return (
        <div
            style={{
                display: 'flex',
                height: `${62 * 12}px`,
                overflowY: 'scroll',
                position: 'relative',
                scrollbarWidth: 'none',
            }}
            ref={scrollRef}
        >
            {/* hours column */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: '30px',
                height: `${62 * 25}px`,
            }}>
                {/* events row */}
                <Box
                    sx={{
                        width: '1250px',
                        marginRight: '-1210px',
                        textAlign: 'left',
                        alignContent: 'flex-start',
                        color: black,
                        fontSize: '12px',
                        height: '60px',
                        fontFamily: 'IBM Plex Sans',
                        paddingRight: '5px',
                        borderBottom: `1px solid ${black}4D`,
                        zIndex: 2,
                        pointerEvents: 'none'
                    }}
                >
                    Events
                </Box>
                {/* hours column */}
                {hours.map((hour, index) => {
                    return (
                        <Box
                            key={`${hour}${index}`}
                            sx={{
                                width: '1250px',
                                marginRight: '-1210px',
                                textAlign: 'left',
                                alignContent: 'flex-start',
                                color: index === currentHour ? primary500 : black,
                                fontSize: '12px',
                                height: '60px',
                                fontFamily: 'IBM Plex Sans',
                                paddingRight: '5px',
                                borderBottom: `1px solid ${index === currentHour - 1 ? primary500 : black}4D`,
                                zIndex: 2,
                                pointerEvents: 'none'
                            }}
                        >
                            {formatHour(hour)}
                        </Box>
                    )
                })}
            </div>
            {/* day and schedules */}
            <Box sx={{
                display: 'grid',
                width: '1200px',
                gridTemplateColumns: columns
                    .map((_, i) => (expanded ? i === expandedIndex ? `${columnWidthExpand}px` : `${columnWidthShrink}px` : `${columnWidthBase}px`))
                    .join(' '),
                justifyContent: 'center',
                rowGap: '0px',
                height: '21px',
                borderBottom: '1px solid black',
                paddingBottom: '5px',
                transition: 'all 0.3s ease',
            }}>
                {/* Days of the Week Headers */}
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun',].map((day, index) => {
                    const dayDate = weekDates[index];
                    const isOtherMonth = dayDate.month() !== month;
                    const isOtherYear = dayDate.year() !== year;
                    const dateNum = isOtherMonth || isOtherYear ?
                        `${dayDate.format('MMM')} ${dayDate.format('D')}, ${isOtherYear ? `${dayDate.format('YYYY')}` : ''}`
                        : `${dayDate.format('D')}`;
                    const isToday = dayDate.isToday();

                    return (
                        <Typography
                            key={day}
                            variant="subtitle1"
                            sx={{
                                textAlign: 'center',
                                color: isToday ? primary500 : neutral400,
                            }}
                        >
                            {day} {dateNum}
                        </Typography>
                    );
                })}
                {/* Events */}
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun',].map((day, index) => {
                    const dayDateString = weekDateStrings[index];
                    const dayEvents = events.filter(event => event.date === dayDateString);
                    const allEventNames = Array.isArray(dayEvents) && dayEvents !== undefined
                        ? dayEvents.map(e => e.title).join(" + ")
                        : 'Event';

                    return (
                        <Box
                            key={`${day}+${index}`}
                            sx={{
                                borderRight: day !== 'Sun' ? `2px solid ${black}` : 'none',
                                height: '60px',
                                zIndex: 2
                            }}
                        >
                            {expanded === true && expandedIndex === index && dayEvents.length > 0 ?
                                dayEvents.map((event, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: "flex",
                                            padding: "2px 8px",
                                            margin: "2px", // optional spacing between boxes
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: "40px",
                                            border: `1px solid rgba(39, 137, 255, 0.10)`,
                                            background: `linear-gradient(0deg, rgba(39, 137, 255, 0.10) 0%, rgba(39, 137, 255, 0.10) 100%), #FFF;`,
                                            transition: "background 0.3s ease, color 0.3s ease",
                                            zIndex: 5,
                                            "&:hover": {
                                                backgroundColor: "#B8E0FF", // Slightly darker blue on hover
                                                transform: "translateY(-2px)", // Lift the card slightly
                                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                                                "& .on-text": {
                                                    color: black,
                                                },
                                                cursor: "pointer",
                                                zIndex: 6,
                                            },
                                        }}
                                        onClick={() => {
                                            if (expanded === true && expandedIndex === index) {
                                                setIsEventEditModalOpen(true);
                                                setSelectedEvent(event);
                                            } else {
                                                setExpandedIndex(index);
                                                setExpanded(true);
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            className="on-text"
                                            sx={{
                                                color: "#2789FF",
                                                fontSize: "12px",
                                                wordWrap: "break-word",
                                                overflowWrap: "break-word",
                                                whiteSpace: "normal",
                                            }}
                                        >
                                            {event.title}
                                        </Typography>
                                    </Box>
                                ))
                                :
                                dayEvents.length > 0 &&
                                <Box
                                    sx={{
                                        display: "flex",
                                        // width: width,
                                        padding: "2px 0", // Reduce padding for smaller appearance
                                        justifyContent: "center",
                                        alignItems: "center",
                                        borderRadius: "40px",
                                        border: `1px solid rgba(39, 137, 255, 0.10)`,
                                        background: `linear-gradient(0deg, rgba(39, 137, 255, 0.10) 0%, rgba(39, 137, 255, 0.10) 100%), #FFF;`,
                                        transition: "background 0.3s ease, color 0.3s ease",
                                        zIndex: 5,
                                        "&:hover": {
                                            backgroundColor: "#B8E0FF", // Slightly darker blue on hover
                                            transform: "translateY(-2px)", // Lift the card slightly
                                            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                                            "& .on-text": {
                                                color: black,
                                            },
                                            cursor: 'pointer',
                                        },
                                    }}
                                    onClick={() => {
                                        if (expanded === true && expandedIndex === index) {
                                            setIsEventEditModalOpen(true);
                                            // setSelectedEvent(event);
                                        } else {
                                            setExpandedIndex(index)
                                            setExpanded(true)
                                        };
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        className="on-text"
                                        sx={{
                                            color: "#2789FF",
                                            fontSize: "12px",
                                            wordWrap: 'break-word',
                                            overflowWrap: 'break-word',
                                            whiteSpace: 'normal',
                                        }}
                                    >
                                        {allEventNames}
                                    </Typography>
                                </Box>
                            }
                        </Box>
                    );
                })}
                {/* Schedules */}
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, index) => {
                    let elementWidth = expandedIndex === index ? 80 : 40;
                    const daySchedules = filteredSchedules.filter((schedule) =>
                        schedule.repeat?.includes(day) || false
                    );
                    const groupedByOutlets = new Map();
                    let groupIndex = 0;
                    return (
                        <div
                            key={`${day}schedule`}
                            style={{
                                borderRight: day !== 'Sun' ? `2px solid ${black}` : 'none',
                                height: `${62 * 24}px`
                            }}
                        >
                            <Box sx={{
                                display: "flex",
                                position: "relative",
                            }}>
                                {/* expanded vs condensed schedule view */}
                                {(expandedIndex === index ?
                                    daySchedules
                                        .filter(
                                            (schedule) =>
                                                schedule.status === filter
                                            // &&
                                            //     schedule.outletsSelected.includes(outlet.id)
                                        ).flatMap((schedule) => {
                                            // key based on selected outlets
                                            const normalizedOutletsKey = JSON.stringify([...schedule.outletsSelected].sort());
                                            // set group index based on selected outlets key
                                            if (!groupedByOutlets.has(normalizedOutletsKey)) {
                                                groupedByOutlets.set(normalizedOutletsKey, groupIndex++);
                                            };
                                            const groupMarginIndex = groupedByOutlets.get(normalizedOutletsKey);
                                            // sets type of schedule display
                                            if ((schedule.onHour + timezoneHourDifference > schedule.offHour + timezoneHourDifference && schedule.offHour + timezoneHourDifference !== 0 && schedule.offHour + timezoneHourDifference > 0 && schedule.onHour + timezoneHourDifference < 24) || schedule.offHour + timezoneHourDifference > 24) {
                                                // Case when the schedule spans across two days
                                                return [
                                                    {
                                                        id: `${schedule.id}-day1`, // Unique key for first part
                                                        start: schedule.onHour + timezoneHourDifference,
                                                        end: 24, // Until the end of the day
                                                        status: schedule.status,
                                                        schedule: schedule,
                                                        groupMarginIndex: groupMarginIndex
                                                    },
                                                    {
                                                        id: `${schedule.id}-day2`, // Unique key for second part
                                                        start: 0, // Start of the next day
                                                        end: schedule.offHour + timezoneHourDifference > 24 ? schedule.offHour + timezoneHourDifference - 24 : schedule.offHour + timezoneHourDifference,
                                                        status: schedule.status,
                                                        schedule: schedule,
                                                        groupMarginIndex: groupMarginIndex
                                                    },
                                                ];
                                            } else {
                                                // Regular case when the schedule is within the same day
                                                return [
                                                    {
                                                        id: schedule.id,
                                                        start: (schedule.onHour + timezoneHourDifference <= 0) ? 0 : (schedule.onHour + timezoneHourDifference >= 24) ? schedule.onHour + timezoneHourDifference - 24 : schedule.onHour + timezoneHourDifference,
                                                        end: (schedule.offHour + timezoneHourDifference <= 0) ? 24 : schedule.offHour + timezoneHourDifference,
                                                        status: schedule.status,
                                                        schedule: schedule,
                                                        groupMarginIndex: groupMarginIndex
                                                    },
                                                ];
                                            };
                                            // add's array name for display purposed
                                        }).map(item => ({
                                            ...item,
                                            schedules: [item.schedule],
                                            ids: [item.id]
                                        }))
                                    : Object.values(daySchedules
                                        .filter(
                                            (schedule) =>
                                                schedule.status === filter
                                            // &&
                                            //     schedule.outletsSelected.includes(outlet.id)
                                        ).flatMap((schedule) => {
                                            // key based on selected outlets

                                            const normalizedOutletsKey = JSON.stringify([...schedule.outletsSelected].sort());
                                            // set group index based on selected outlets key
                                            if (!groupedByOutlets.has(normalizedOutletsKey)) {
                                                groupedByOutlets.set(normalizedOutletsKey, groupIndex++);
                                            };
                                            const groupMarginIndex = groupedByOutlets.get(normalizedOutletsKey);
                                            // sets type of schedule display
                                            if ((schedule.onHour + timezoneHourDifference > schedule.offHour + timezoneHourDifference && schedule.offHour + timezoneHourDifference !== 0 && schedule.offHour + timezoneHourDifference > 0 && schedule.onHour + timezoneHourDifference < 24) || schedule.offHour + timezoneHourDifference > 24) {
                                                // Case when the schedule spans across two days
                                                return [
                                                    {
                                                        id: `${schedule.id}-day1`, // Unique key for first part
                                                        start: schedule.onHour + timezoneHourDifference,
                                                        end: 24, // Until the end of the day
                                                        status: schedule.status,
                                                        schedule: schedule,
                                                        groupMarginIndex: groupMarginIndex
                                                    },
                                                    {
                                                        id: `${schedule.id}-day2`, // Unique key for second part
                                                        start: 0, // Start of the next day
                                                        end: schedule.offHour + timezoneHourDifference > 24 ? schedule.offHour + timezoneHourDifference - 24 : schedule.offHour + timezoneHourDifference,
                                                        status: schedule.status,
                                                        schedule: schedule,
                                                        groupMarginIndex: groupMarginIndex
                                                    },
                                                ];
                                            } else {
                                                // Regular case when the schedule is within the same day
                                                return [
                                                    {
                                                        id: schedule.id,
                                                        start: (schedule.onHour + timezoneHourDifference <= 0) ? 0 : (schedule.onHour + timezoneHourDifference >= 24) ? schedule.onHour + timezoneHourDifference - 24 : schedule.onHour + timezoneHourDifference,
                                                        end: (schedule.offHour + timezoneHourDifference <= 0) ? 24 : schedule.offHour + timezoneHourDifference,
                                                        status: schedule.status,
                                                        schedule: schedule,
                                                        groupMarginIndex: groupMarginIndex
                                                    },
                                                ];
                                            };
                                        }).reduce((acc, curr) => {
                                            const key = `${curr.start}-${curr.end}`;
                                            if (!acc[key]) {
                                                acc[key] = {
                                                    ...curr,
                                                    schedules: [curr.schedule],
                                                    ids: [curr.id]
                                                };
                                            } else {
                                                acc[key].schedules.push(curr.schedule);
                                                acc[key].ids.push(curr.id);
                                            };
                                            return acc;
                                        }, {})
                                    )).map(({ ids, start, end, status, schedules, groupMarginIndex, schedule, id }, idx, compressedKeys) => {
                                        const color = status === "Accepted" ? primary500 : "#2789FF";
                                        const allMissionIds = Array.isArray(schedules) && schedules !== undefined
                                            ? schedules.map(s => s.missionId).join(" + ")
                                            : schedule.missionId;

                                        const groupedSize = groupedByOutlets.size;
                                        // dynamically set schedule width and position
                                        elementWidth = expanded ?
                                            expandedIndex === index ? (columnWidthExpand / groupedByOutlets.size) - 2 : (columnWidthShrink / Math.min(compressedKeys.length, groupedSize)) - 2
                                            : (columnWidthBase / compressedKeys.length) - 2;
                                        const maxWidth = Math.max(elementWidth, 30)
                                        const marginLeft = elementWidth * Math.min(groupMarginIndex, idx);

                                        return (
                                            <Tooltip
                                                key={`${ids.join("-")}Tooltip${day}`}
                                                title={allMissionIds}
                                                followCursor
                                                arrow
                                                placement="top"
                                                enterDelay={200}
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            backgroundColor: neutral400,
                                                            color: "white",
                                                            fontSize: "16px",
                                                            borderRadius: "8px",
                                                            padding: "8px 12px",
                                                            maxWidth: 300,
                                                            whiteSpace: 'pre-line', // allows line breaks in strings
                                                        },
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: neutral400,
                                                        },
                                                    },
                                                }}
                                            >
                                                <Box
                                                    key={ids.join("-")}
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: 'row',
                                                        height: `${(end - start) * 61 - 3}px`,
                                                        width: `${maxWidth - 2}px`,
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        borderRadius: "8px",
                                                        border: `1px solid ${color}`,
                                                        background:
                                                            status === "Accepted"
                                                                ? `linear-gradient(0deg, rgba(229, 9, 20, 0.10) 0%, rgba(229, 9, 20, 0.10) 100%), ${white}`
                                                                : `linear-gradient(0deg, rgba(39, 137, 255, 0.10) 0%, rgba(39, 137, 255, 0.10) 100%), #FFF;`,
                                                        transition: "background 0.3s ease, color 0.3s ease",
                                                        position: "absolute",
                                                        marginLeft: `${marginLeft + 1}px`,
                                                        "&:hover": {
                                                            background: `linear-gradient(0deg, ${color} 0%, ${color} 100%), ${white}`,
                                                            "& .on-text": {
                                                                color: white,
                                                            },
                                                            cursor: 'pointer',
                                                            zIndex: 10
                                                        },
                                                        top: `${(start < 0 ? 0 : start * 61) + 5}px`
                                                    }}
                                                    onClick={() => {
                                                        if (expanded === true && expandedIndex === index) {
                                                            scheduleClick(schedule);
                                                        } else {
                                                            setExpandedIndex(index)
                                                            setExpanded(true)
                                                        };
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        className="on-text"
                                                        sx={{
                                                            color: color,
                                                            fontSize: "12px",
                                                            width: `${elementWidth - 2}px`,
                                                            wordWrap: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            whiteSpace: 'normal',
                                                            height: `${(end - start) * 61 - 3}px`,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {allMissionIds}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        );
                                    })
                                }
                            </Box>
                        </div>
                    );
                })}
                {/* dates on the bottom */}
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun',].map((day, index) => {
                    const dayDate = weekDates[index];
                    const isOtherMonth = dayDate.month() !== month;
                    const isOtherYear = dayDate.year() !== year;
                    const dateNum = isOtherMonth || isOtherYear ?
                        `${dayDate.format('MMM')} ${dayDate.format('D')}, ${isOtherYear ? `${dayDate.format('YYYY')}` : ''}`
                        : `${dayDate.format('D')}`;
                    const isToday = dayDate.isToday();

                    return (
                        <Typography
                            key={day}
                            variant="subtitle1"
                            sx={{
                                textAlign: 'center',
                                color: isToday ? primary500 : neutral400,
                                borderTop: '1px solid black',
                                paddingTop: '5px',
                            }}
                        >
                            {day} {dateNum}
                        </Typography>
                    );
                })}
            </Box>
        </div>
    );
};